// src/lib/sat/answer-validation.ts
//
// Server-side answer validation for /api/sat/save (#825, revives #748).
//
// Why this exists: the save route used to store whatever the client sent and
// then grade it. Two grading-corruption bugs followed:
//
//   1. "Marked wrong on a skip" — an empty / whitespace `choice` sent on a
//      skip was stored and graded `correct:false` (since `"".trim() !== "B"`),
//      so a skipped question counted as wrong instead of staying ungraded.
//   2. Junk SPR text ("abc", a 40-char paste) was stored verbatim and fed to
//      the grader, polluting the response and the score report.
//
// These pure validators run BEFORE any DB write so a rejected save creates or
// mutates nothing. They are deliberately format-only — correctness is still
// decided by the shared grader (gradeAnswer / gradeSpr).

import { isStoredChoice } from '@/lib/test-engine/normalize-choices'
import { sprEntryProblem } from '@unlimitedtests/core'

// The grid-in entry rules (the 5/6-character cap, the format check and the
// copy a student sees) moved to @unlimitedtests/core so the mobile app blocks
// exactly the entries the server would. Re-exported for the existing callers.
export {
  validateNumericAnswer,
  sprEntryProblem,
  SPR_FORMAT_MESSAGE,
  SPR_LENGTH_MESSAGE,
  SPR_EMPTY_MESSAGE,
} from '@unlimitedtests/core'

/**
 * Canonical Digital-SAT MCQ label set. Used as the allowed set when a
 * question's own choices aren't in the canonical {label,text}[] shape (or
 * aren't available). MCQ items are uniformly four choices A–D.
 */
export const DEFAULT_MCQ_LABELS = ['A', 'B', 'C', 'D'] as const

export type AnswerValidation = { ok: true } | { ok: false; error: string }

/**
 * Extract the allowed MCQ labels from a `Question.choices` payload, or null
 * when the payload isn't the canonical {label,text}[] shape (caller falls back
 * to DEFAULT_MCQ_LABELS).
 */
export function allowedChoiceLabels(choices: unknown): string[] | null {
  if (!Array.isArray(choices)) return null
  const labels = choices.filter(isStoredChoice).map((c) => c.label)
  return labels.length > 0 ? labels : null
}

/**
 * Validate an MCQ choice label. Rejects non-strings, empty / whitespace-only
 * input, and any label outside the question's allowed set.
 */
export function validateChoice(choice: unknown, allowed: readonly string[]): AnswerValidation {
  if (typeof choice !== 'string') return { ok: false, error: 'choice must be a string' }
  const t = choice.trim()
  if (t === '') return { ok: false, error: 'empty choice' }
  if (!allowed.includes(t)) return { ok: false, error: 'choice not in allowed set' }
  return { ok: true }
}

// ---------------------------------------------------------------------------
// Practice-surface guard (QA: "Invalid numeric answers are graded as wrong in
// practice").
//
// The timed runner never grades junk: /api/sat/save runs validateNumericAnswer
// first and the Runner shows a live "Not a recognized number" warning. The
// practice surfaces (topic practice, adaptive, mini drills, review) had no such
// gate — "abc" on a grid-in went straight to the grader, came back `false`, and
// was recorded as a MISS: TopicProgress (accuracy / mastery), a review card,
// XP/streak, and a Mistake DNA concept gap, for what was a typo.
//
// These helpers put the SAME validator in front of those surfaces, client and
// server, so an unreadable grid-in entry is an input error, never a wrong
// answer. "I don't know" is not an entry and never reaches them.
// ---------------------------------------------------------------------------

/**
 * Machine-readable marker on a practice route's 400 for an unreadable grid-in
 * entry, so the client shows the message inline instead of a generic failure.
 */
export const INVALID_ANSWER_FORMAT = 'INVALID_ANSWER_FORMAT' as const

/**
 * The problem with a submitted practice answer, or null when it may be graded.
 * Only grid-in (SPR) entries are checked; MCQ labels are left to the grader.
 * Callers skip this for "I don't know", which carries no entry at all.
 */
export function practiceAnswerProblem(
  itemType: string | null | undefined,
  answer: unknown,
): string | null {
  if (itemType !== 'SPR') return null
  return sprEntryProblem(answer)
}
